import { z } from 'zod';
import { API_BASE, apiGet, apiPost } from './api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  WYSŁANIE PLIKU NA MASZYNĘ — częściami, bo inaczej nie dojedzie.
 *
 *  ⚠️⚠️ ŚCIANA SZEŚĆDZIESIĘCIU SEKUND JEST ZMIERZONA, nie przypuszczana: brzeg platformy przerywa
 *  wymianę po minucie (HTTP 504). Plik wysyłany jednym żądaniem nie dojedzie NIGDY, ile by nie
 *  powtarzać — dokładnie to zobaczyliśmy przy kopiach z maszyn w górę (32,1 MB z 81,2 — URWANE
 *  w 60,1 s). Podział jest więc warunkiem działania, a nie usprawnieniem.
 *
 *  ⚠️⚠️ WZNAWIAMY OD MIEJSCA, W KTÓRYM PADŁO. Przed każdą próbą pytamy instancję, które części
 *  już u niej leżą — i porównujemy ROZMIAR, nie samą obecność: część urwana w połowie też ma swój
 *  obiekt, tylko krótszy. Bez tego porównania wznowienie skleiłoby plik z dziurą w środku.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

const RozpoczetaSchema = z.object({
  przesylka: z.string(),
  nazwa: z.string(),
  czescB: z.number(),
  czesci: z.number(),
});

const CzesciSchema = z.object({
  czesci: z.array(z.object({ numer: z.number(), rozmiar: z.number() })),
});

const ZlozonaSchema = z.object({
  taskId: z.string(),
  nazwa: z.string(),
  sha256: z.string(),
  rozmiar: z.number(),
});

export type CzescPliku = { numer: number; od: number; do: number };

/** Podział pliku na części — czysty, bo to on decyduje, czy plik da się potem złożyć. */
export function podzialPliku(rozmiar: number, czescB: number): CzescPliku[] {
  if (!Number.isFinite(rozmiar) || rozmiar <= 0 || !Number.isFinite(czescB) || czescB <= 0) return [];
  const czesci: CzescPliku[] = [];
  for (let od = 0, n = 1; od < rozmiar; od += czescB, n++) {
    czesci.push({ numer: n, od, do: Math.min(od + czescB, rozmiar) });
  }
  return czesci;
}

/**
 * Które części trzeba jeszcze wysłać.
 *
 * ⚠️⚠️ PORÓWNUJEMY ROZMIAR, NIE OBECNOŚĆ. Część przerwana w połowie leży w magazynie jako krótszy
 * obiekt — uznanie jej za gotową dałoby plik z dziurą, który złoży się bez błędu i dopiero suma
 * kontrolna na maszynie powie, że coś jest nie tak. Albo nie powie, bo nikt jej nie sprawdzi.
 */
export function doWyslania(
  czesci: readonly CzescPliku[],
  gotowe: readonly { numer: number; rozmiar: number }[],
): CzescPliku[] {
  const mam = new Map(gotowe.map((g) => [g.numer, g.rozmiar]));
  return czesci.filter((c) => mam.get(c.numer) !== c.do - c.od);
}

export type PostepWysylki = { wyslanoB: number; wszystkiegoB: number; czesc: number; czesci: number };

/**
 * ⚠️ TRZY PRÓBY NA CZĘŚĆ — tyle samo, co przy wysyłce kopii z agenta. Łącze LTE gubi połączenie
 * regularnie i bez ponowień pojedyncze potknięcie kończyłoby całą wysyłkę.
 */
const PROB_NA_CZESC = 3;

export async function wyslijPlikNaMaszyne(opts: {
  groupId: string;
  machineId: string;
  plik: File;
  postep?: (p: PostepWysylki) => void;
  przerwane?: () => boolean;
}): Promise<z.infer<typeof ZlozonaSchema>> {
  const start = await apiPost(
    '/pliki/rozpocznij',
    {
      groupId: opts.groupId,
      machineId: opts.machineId,
      nazwa: opts.plik.name,
      rozmiar: opts.plik.size,
    },
    RozpoczetaSchema,
  );

  const czesci = podzialPliku(opts.plik.size, start.czescB);
  const juzSa = await apiGet(
    `/pliki/${start.przesylka}/czesci?groupId=${encodeURIComponent(opts.groupId)}`,
    CzesciSchema,
  );

  let wyslanoB = juzSa.czesci.reduce((a, c) => a + c.rozmiar, 0);
  const zostalo = doWyslania(czesci, juzSa.czesci);

  for (const c of zostalo) {
    if (opts.przerwane?.() === true) throw new Error('Wysyłka przerwana');

    let ostatniBlad: unknown = null;
    for (let proba = 1; proba <= PROB_NA_CZESC; proba++) {
      try {
        const odp = await fetch(
          `${API_BASE}/pliki/${start.przesylka}/czesc?groupId=${encodeURIComponent(opts.groupId)}&n=${c.numer}`,
          {
            method: 'PUT',
            credentials: 'include',
            /** ⚠️ Typ MUSI być jednym z tych, które zna parser surowego ciała w API — inaczej 415. */
            headers: { 'Content-Type': 'application/octet-stream' },
            body: opts.plik.slice(c.od, c.do),
          },
        );
        if (!odp.ok) throw new Error(await opisBledu(odp));
        ostatniBlad = null;
        break;
      } catch (e) {
        ostatniBlad = e;
        /** ⚠️ Odstęp rośnie — przy zerwanym łączu trzy próby pod rząd to trzy razy ten sam wynik. */
        if (proba < PROB_NA_CZESC) await chwila(1000 * proba);
      }
    }
    if (ostatniBlad !== null) throw ostatniBlad;

    wyslanoB += c.do - c.od;
    opts.postep?.({ wyslanoB, wszystkiegoB: opts.plik.size, czesc: c.numer, czesci: czesci.length });
  }

  return apiPost(
    `/pliki/${start.przesylka}/zloz`,
    { groupId: opts.groupId, czesci: czesci.length },
    ZlozonaSchema,
  );
}

const chwila = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** ⚠️ Treść odmowy z instancji, a nie samo „HTTP 400" — to ona mówi, co jest nie tak z plikiem. */
async function opisBledu(odp: Response): Promise<string> {
  try {
    const tresc = (await odp.json()) as { message?: unknown };
    if (typeof tresc.message === 'string') return tresc.message;
  } catch {
    /* odpowiedź bez JSON-a — zostaje sam kod */
  }
  return `Instancja odrzuciła część (HTTP ${odp.status})`;
}
